'use client';

import { PlanConfirmationModal } from 'components';
import type { PlanPeriod, ActionResult, RecurlyPlanData, FinalPlan, PlanTier, InitialPlanState } from 'components';
import { useState, useEffect, useCallback, JSX } from 'react';
import type { BillingInfo, Subscription } from 'subscription-service-client';
import { SubscriptionStatus } from 'subscription-service-client';

import { Dialog, DialogContent, DialogTitle, Text } from '@videoblocks/design-system';
import { CheckCircle } from '@videoblocks/react-icons';

import UpgradeCreditPlanCard from './UpgradeCreditPlanCard/UpgradeCreditPlanCard';
import { isCurrentUserPlan } from './utils/isCurrentUserPlan';

interface UpgradeCreditPlanTableProps {
    plans: FinalPlan[];
    selectedPeriod: PlanPeriod;
    currencySymbol: string;
    currencyCode: string;
    subscriptionId: string;
    billingInfo: BillingInfo | null;
    redirectAfterRegister?: string;
    initialPlanState?: InitialPlanState | null;
    currentBillingAmount: number | null;
    subscriptionData?: Subscription | null;
}

export default function UpgradeCreditPlanTable({
    plans,
    selectedPeriod,
    currencySymbol,
    currencyCode,
    subscriptionId,
    billingInfo,
    redirectAfterRegister,
    initialPlanState,
    currentBillingAmount,
    subscriptionData,
}: UpgradeCreditPlanTableProps): JSX.Element {
    const [selectedPlanForUpgrade, setSelectedPlanForUpgrade] = useState<FinalPlan | null>(
        initialPlanState?.currentPlan || null,
    );
    const [selectedTier, setSelectedTier] = useState<PlanTier | null>(initialPlanState?.selectedTier || null);
    const [addOnPlanCodeSelected, setAddOnPlanCodeSelected] = useState<string | undefined>(
        initialPlanState?.selectedAddon,
    );
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [upgradedPlan, setUpgradedPlan] = useState<{ planId: string; period: PlanPeriod } | null>(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [referrerPage, setReferrerPage] = useState<string>('');
    const [countdownSeconds, setCountdownSeconds] = useState(3);

    const handleToRedirect = useCallback(() => {
        const redirectTo =
            redirectAfterRegister ||
            (referrerPage === window.location.pathname ? '/member/billing' : referrerPage) ||
            '/member/billing';
        const finalUrl =
            redirectTo.startsWith('/') || redirectTo.startsWith('http')
                ? redirectTo.replaceAll('http:', 'https:')
                : `/${redirectTo}`;
        window.location.href = finalUrl;
    }, [redirectAfterRegister, referrerPage]);

    const showCountdownAndRedirect = useCallback(() => {
        const interval = setInterval(() => {
            setCountdownSeconds((seconds: number) => {
                if (seconds < 0) {
                    clearInterval(interval);
                    handleToRedirect();
                    return 0;
                }
                return seconds - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [handleToRedirect]);

    useEffect(() => {
        if (showSuccessModal) {
            showCountdownAndRedirect();
        }
    }, [showSuccessModal, showCountdownAndRedirect]);

    // Check if subscription is cancelled or expired
    const isCancelledOrExpired =
        subscriptionData?.status === SubscriptionStatus.Canceled || subscriptionData?.status === 'expired';

    // Track where user came from for redirect after success
    useEffect(() => {
        // Priority 1: Check query param
        const params = new URLSearchParams(window.location.search);
        const fromParam = params.get('from');

        if (fromParam) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setReferrerPage(fromParam);
        } else {
            // Priority 2: Check document.referrer
            const referrer = document.referrer;
            if (referrer && referrer.includes(window.location.hostname)) {
                const path = new URL(referrer).pathname;
                setReferrerPage(path);
            }
        }
    }, []);

    const handleSelectTier = (plan: FinalPlan, planCode: string | undefined, credits: number) => {
        if (planCode === undefined) return;
        setSelectedTier((prev) => {
            if (prev?.planId === plan.id && prev?.planCode === planCode && prev?.credits === credits) {
                return prev;
            }

            return { planId: plan.id, planCode, credits };
        });

        setSelectedPlanForUpgrade(plan);
        setAddOnPlanCodeSelected(planCode);
    };

    /**
     * Handles the status update after a plan change attempt
     * @param result - The result of the subscription change action
     * @param planId - The ID of the plan that was being changed to
     */
    const handleStatusUpdate = useCallback(
        (result: ActionResult, planId: string) => {
            if (result.success) {
                // 1. Close modal immediately
                setIsModalOpen(false);

                // 2. Update upgraded plan state
                setUpgradedPlan({ planId, period: selectedPeriod });

                // 3. Find the plan to get details for success message
                const plan = plans.find((p) => p.id === planId);
                const planName = plan?.name || 'your plan';

                // Format plan name: remove period indicator like "(Annual)" or "(Monthly)"
                const formattedPlanName = planName.replace(/\s*\([^)]*\)/, '');

                const credits = selectedTier?.credits || 0;
                const periodText = selectedPeriod === 'annual' ? 'Annual' : 'Monthly';
                const message = result.activated
                    ? `Successfully upgraded to ${formattedPlanName} ${periodText} plan with ${credits.toLocaleString()} credits/month.`
                    : result.message;

                setSuccessMessage(message);
                setShowSuccessModal(true);
            }
        },
        [selectedPeriod, selectedTier, plans],
    );

    const handleCloseModal = () => setIsModalOpen(false);

    const handleUpgradeClick = (plan: FinalPlan) => {
        if (!addOnPlanCodeSelected) {
            return;
        }

        setSelectedPlanForUpgrade(plan);
        setIsModalOpen(true);
    };

    return (
        <div>
            <div className="overflow-x-auto">
                <div className="rounded-lg p-8 shadow-xl">
                    <div className="flex flex-col lg:flex-row justify-center gap-4 md:gap-6 items-stretch w-full mx-auto overflow-x-auto px-4">
                        {plans
                            .filter((plan) => {
                                // This filters is due to an arrangement to help UX hiding plans that doesn't have acces to voiceover
                                // For now we will just hide hard coded way, but we should update this to
                                // a more dynamic subscription-service reliant way

                                // Check if user came from voiceover page via referrer or redirectAfterRegister param
                                const isFromVoiceover =
                                    referrerPage.startsWith('/voiceover') ||
                                    (redirectAfterRegister &&
                                        decodeURIComponent(redirectAfterRegister).includes('/voiceover'));

                                return isFromVoiceover
                                    ? !['4015', '4020', '4025', '4030', '4035', '4040'].includes(
                                          plan.planCodes[selectedPeriod] ?? '',
                                      )
                                    : true;
                            })
                            .map((plan) => {
                                const isCurrentPlan = isCurrentUserPlan(plan, selectedPeriod);
                                if (
                                    (selectedPeriod === 'monthly' && !plan.pricing.monthly) ||
                                    (selectedPeriod === 'annual' && !plan.pricing.annual)
                                ) {
                                    return null;
                                }

                                let updatedPlan = {
                                    ...plan,
                                    currentUserPlan: {
                                        isCurrentMonthly: false,
                                        isCurrentAnnual: false,
                                    },
                                };

                                if (upgradedPlan) {
                                    if (upgradedPlan.planId === plan.id) {
                                        updatedPlan = {
                                            ...plan,
                                            currentUserPlan: {
                                                isCurrentMonthly: upgradedPlan.period === 'monthly',
                                                isCurrentAnnual: upgradedPlan.period === 'annual',
                                            },
                                        };
                                    }
                                }

                                return (
                                    <div key={plan.id} className="flex flex-wrap justify-center items-stretch gap-6">
                                        <UpgradeCreditPlanCard
                                            plan={plan}
                                            selectedPeriod={selectedPeriod}
                                            currencySymbol={currencySymbol}
                                            isCurrentPlan={isCurrentPlan}
                                            isCancelledOrExpired={isCancelledOrExpired}
                                            onSelectTier={(planCode, credits) =>
                                                handleSelectTier(plan, planCode, credits)
                                            }
                                            onUpgradePlan={() => handleUpgradeClick(updatedPlan)}
                                            selectedTier={selectedTier?.planId === plan.id ? selectedTier : null}
                                            currentBillingAmount={currentBillingAmount}
                                            currentAddonCode={initialPlanState?.selectedAddon}
                                        />
                                    </div>
                                );
                            })}
                    </div>
                </div>
            </div>
            {showSuccessModal && (
                <Dialog open>
                    <DialogContent showCloseButton={false} className="flex flex-col items-center p-8">
                        <CheckCircle className="h-12 w-12 fill-green-600" />
                        <DialogTitle>
                            <Text fontSize="sm">{successMessage}</Text>
                        </DialogTitle>
                        <Text fontSize="xs" fontWeight="regular">
                            {countdownSeconds > 0
                                ? `You are going to be redirected in ${countdownSeconds}...`
                                : 'Redirecting...'}
                        </Text>
                    </DialogContent>
                </Dialog>
            )}
            <PlanConfirmationModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                plan={selectedPlanForUpgrade}
                selectedPeriod={selectedPeriod}
                currencySymbol={currencySymbol}
                currencyCode={currencyCode}
                subscriptionId={subscriptionId}
                billingInfo={billingInfo}
                onStatusUpdate={(result) => {
                    handleStatusUpdate(result, selectedPlanForUpgrade?.id || '');
                }}
                addOnPlanCodeSelected={addOnPlanCodeSelected}
                currentBillingAmount={currentBillingAmount}
                subscriptionData={subscriptionData}
            />
        </div>
    );
}
